// ============================================================
// OutboundConfirmCard — «erst zeigen, dann senden».
// ============================================================
// Der Assistent stellt Kundennachrichten nur noch FERTIG; abgeschickt werden
// sie hier, vom Menschen. Die Karte zeigt Kanal, Empfänger, Betreff und den
// VOLLEN Text — damit niemand mehr «ich habe geantwortet und gesendet» hört,
// ohne den Text je gesehen zu haben.
//
// Sie hängt in der AssistantBar und damit auf BEIDEN Oberflächen (PlanMove
// mobil, Boss Pilot Desktop) — ein eigener Handler pro Shell wäre nicht nur
// doppelt, sondern hätte auch Lücken: eine Nachricht darf nicht deshalb
// ungezeigt rausgehen, weil gerade der falsche Screen offen ist.
//
// Der zweite Bestätigungsweg ist mündlich (Werkzeug confirm_outbound); beide
// landen im selben Server-Gate (backend/.../outbound-confirm.service.ts).
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { onUiCommand, type UiCommand } from '../../../core/uiCommands.ts';
import { authedFetch, errDetail } from '../../../components/assistant-bar/engine/api.ts';
import { useTranslation } from '../../lib/useTranslation.ts';
import './outbound-confirm.css';

type Pending = Extract<UiCommand, { type: 'CONFIRM_OUTBOUND' }>;
type Phase = 'idle' | 'sending' | 'sent' | 'error';

export function OutboundConfirmCard() {
  const t = useTranslation();
  const [pending, setPending] = useState<Pending | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState('');

  useEffect(() => onUiCommand('CONFIRM_OUTBOUND', (cmd) => {
    setPending(cmd);
    setPhase('idle');
    setError('');
    return true;
  }), []);

  // Mündlich bestätigt/verworfen (confirm_outbound / cancel_outbound):
  // der Server hat schon gehandelt, hier bleibt nur die Karte wegzuräumen.
  useEffect(() => onUiCommand('OUTBOUND_RESOLVED', (cmd) => {
    setPending((cur) => {
      if (!cur) return cur;
      if (cmd.outboundId && cmd.outboundId !== cur.outboundId) return cur;
      return null;
    });
    setPhase('idle');
    setError('');
    return true;
  }), []);

  const close = useCallback(() => { setPending(null); setPhase('idle'); setError(''); }, []);

  // Abbrechen meldet dem Server, dass der Entwurf tot ist — sonst könnte ein
  // späteres «ja, schick es» ihn wiederbeleben, obwohl der Nutzer ihn hier
  // schon weggeklickt hat.
  const cancel = useCallback(async () => {
    const id = pending?.outboundId;
    close();
    if (!id) return;
    try {
      await authedFetch(`/api/voice/outbound/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
    } catch { /* Der Entwurf verfällt ohnehin von selbst. */ }
  }, [pending, close]);

  const send = useCallback(async () => {
    if (!pending || phase === 'sending') return;
    setPhase('sending');
    setError('');
    try {
      const res = await authedFetch(`/api/voice/outbound/${encodeURIComponent(pending.outboundId)}/send`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        setPhase('error');
        setError(errDetail(data?.error) || t('assistant.outbound.failed'));
        return;
      }
      setPhase('sent');
      // Kurz stehen lassen, damit der Nutzer die Bestätigung sieht.
      setTimeout(() => { setPending((cur) => (cur && cur.outboundId === pending.outboundId ? null : cur)); }, 1600);
    } catch (err) {
      setPhase('error');
      setError(errDetail(err) || t('assistant.outbound.failed'));
    }
  }, [pending, phase, t]);

  if (!pending) return null;

  const isDocument = pending.kind === 'DOCUMENT';
  const title = isDocument
    ? t('assistant.outbound.titleDocument')
    : pending.kind === 'REPLY' ? t('assistant.outbound.titleReply') : t('assistant.outbound.titleEmail');

  return createPortal(
    <div className="obc-overlay" onClick={(e) => { if (e.target === e.currentTarget && phase !== 'sending') cancel(); }}>
      <div className="obc-box" role="dialog" aria-modal="true" aria-label={title}>
        <div className="obc-head">
          <div className="obc-title">{title}</div>
          <div className="obc-note">{t('assistant.outbound.notSentYet')}</div>
        </div>

        <div className="obc-field">
          <span className="obc-label">{t('assistant.outbound.to')}</span>
          <span className="obc-value">
            {pending.recipientName ? `${pending.recipientName} · ` : ''}{pending.recipient}
          </span>
        </div>

        {isDocument && pending.documentLabel ? (
          <div className="obc-field">
            <span className="obc-label">{t('assistant.outbound.document')}</span>
            <span className="obc-value">{pending.documentLabel}</span>
          </div>
        ) : null}

        {pending.subject ? (
          <div className="obc-field">
            <span className="obc-label">{t('assistant.outbound.subject')}</span>
            <span className="obc-value">{pending.subject}</span>
          </div>
        ) : null}

        {pending.body ? <div className="obc-body">{pending.body}</div> : null}

        {phase === 'error' ? <div className="obc-error">{error}</div> : null}
        {phase === 'sent' ? <div className="obc-ok">{t('assistant.outbound.sent')}</div> : null}

        <div className="obc-actions">
          <button type="button" className="obc-cancel" onClick={cancel} disabled={phase === 'sending' || phase === 'sent'}>
            {t('common.cancel')}
          </button>
          <button type="button" className="obc-send" onClick={send} disabled={phase === 'sending' || phase === 'sent'}>
            {phase === 'sending' ? t('assistant.outbound.sending') : t('assistant.outbound.sendNow')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
